import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAmplitudeHostAllowed } from '@/scripts/amplitude-tracker.js';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import {
  get,
  getDatabase,
  query,
  ref,
  orderByKey,
  orderByChild,
  limitToLast,
  limitToFirst,
  endBefore,
  startAfter,
  equalTo,
} from 'firebase/database';
import { containsBlockedProfanity } from '@/lib/staticProfanityFilter';
import { REVIEW_SUBMIT_URL, reviewSubmitHeaders } from '@/config/reviewSubmit';
import { ARTICLE_COMMENT_ACTION_URL } from '@/config/articleCommentsApi';
import {
  articleCommentsRef,
  initFirebaseArticleComments,
  isFirebaseArticleCommentsConfigured,
  sanitizeArticleIdForClient,
} from '@/lib/articleCommentsFirebase';
import { FaCheck } from 'react-icons/fa6';
import { VscHeart, VscHeartFilled } from 'react-icons/vsc';
import { HiChevronRight, HiChevronLeft } from 'react-icons/hi2';
import { LiaEdit } from 'react-icons/lia';
import { RiDeleteBinLine } from 'react-icons/ri';
import { cls } from '@/utils';

const localeObj = {
  ko: {
    selectAvatar: '캐릭터를 변경해보세요',
    reviewPh: '댓글을 남겨주세요',
    namePh: '이름을 입력해주세요',
    submit: '댓글 등록',
    submitting: '전송 중…',
    submitError: '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.',
    submitErrorAuth: '로그인이 필요하거나 만료되었습니다. 다시 로그인한 뒤 시도해 주세요.',
    submitErrorBadRequest: '댓글을 전송할 수 없습니다. 내용을 확인한 뒤 다시 시도해 주세요.',
    submitErrorServer: '일시적으로 전송할 수 없습니다. 잠시 후 다시 시도해 주세요.',
    submitErrorNetwork: '연결을 확인한 뒤 다시 시도해 주세요.',
    submitSuccess: '댓글이 전송되었습니다.',
    submitTooShort: '댓글은 2글자 이상 입력해 주세요.',
    nameRequired: '이름을 입력해 주세요.',
    /** Shown as a warning banner when submit is blocked for profanity (not sent). */
    warningProfanityBlocked:
      '부적절한 표현이 포함되어 있어 댓글을 등록할 수 없습니다. 내용을 고친 뒤 다시 시도해 주세요.',
    needLogin: '로그인 후 이용해주세요',
    goToLogin: '로그인',
    commentsNotConfigured: '댓글을 불러올 수 없습니다.',
    sortNewest: '최신순',
    sortPopular: '인기순',
    loadMore: '더 보기',
    like: '좋아요',
    edit: '수정',
    delete: '삭제',
    deleteConfirm: '이 댓글을 삭제할까요?',
    save: '저장',
    cancel: '취소',
    totalComments: '댓글',
    noComments: '아직 댓글이 없습니다.',
    editedLabel: '수정됨',
    showAll: '자세히보기',
    oneCommentOnlyNotice: '댓글은 한 번만 남길 수 있습니다.',
    oneCommentOnlyChecking: '확인 중…',
    tip: '다음 식탁대전에서 만나고 싶은 지역도 함께 남겨주시면 좋아요!',
    instructionTitle1: '공감(♡)을 많이 받을수록 BEST 댓글이 됩니다',
    instruction1: '마음이 가는 댓글에 하트를 꾹 눌러주세요!',
    instructionTitle2: '프로필도 바꿔보세요',
    instruction2: '프로필 이미지를 눌러 이미지를 변경할 수 있어요!',
    likeError: '좋아요 처리 중 오류가 발생했습니다.',
    eventEnd: '이벤트가 종료되었습니다.',
  },
  en: {
    selectAvatar: 'Select your character',
    reviewPh: 'Write a comment',
    namePh: 'Enter your name',
    submit: 'Submit',
    submitting: 'Sending…',
    submitError: 'Something went wrong. Please try again in a moment.',
    submitErrorAuth: 'Please sign in again, then try submitting your comment.',
    submitErrorBadRequest: 'Your comment could not be sent. Check the text and try again.',
    submitErrorServer: 'We could not send your comment right now. Please try again shortly.',
    submitErrorNetwork: 'Check your connection and try again.',
    submitSuccess: 'Your comment was sent.',
    submitTooShort: 'Enter at least 2 characters.',
    nameRequired: 'Please enter your name.',
    warningProfanityBlocked:
      'This comment cannot be posted because it may contain inappropriate language. Please revise your text and try again.',
    needLogin: 'Please login to leave comment',
    goToLogin: 'Login',
    commentsNotConfigured: 'Comments could not be loaded.',
    sortNewest: 'Newest',
    sortPopular: 'Popular',
    loadMore: 'Load more',
    like: 'Like',
    edit: 'Edit',
    delete: 'Delete',
    deleteConfirm: 'Delete this comment?',
    save: 'Save',
    cancel: 'Cancel',
    totalComments: 'Comments',
    noComments: 'No comments yet.',
    editedLabel: 'Edited',
    showAll: 'Read more',
    oneCommentOnlyNotice: 'You can post only one comment.',
    oneCommentOnlyChecking: 'Checking…',
    tip: '다음 식탁대전에서 만나고 싶은 지역도 함께 남겨주시면 좋아요!',
    instructionTitle1: 'More hearts(♡) = BEST comment',
    instruction1: 'Tap the heart on comments you love!',
    instructionTitle2: 'You can also change your profile image!',
    instruction2: 'Find the one that feels most like you',
    likeError: 'Failed to process like.',
    eventEnd: 'This event has ended.',
  },
};

// const PAGE_SIZE = 5;
const COMMENT_TEXT_COLLAPSE_AT = 150;
const MIN_COMMENT_SUBMIT_LENGTH = 2;

const MIN_COMMENTS_FOR_BEST = 2;
const MIN_LIKES_FOR_BEST = 2;

/** Storefront lists only rows where `hidden` is not `true` (omit/`false` = visible; legacy rows without field count as visible). */
function isCommentNodeVisible(c) {
  return c != null && c.hidden !== true;
}

/**
 * BEST pin: only when meta.commentCount >= 2; among comments with likeCount >= 2,
 * highest likes wins; ties → earliest createdAt (smallest ms). Hidden comments excluded.
 */
async function fetchEligibleBestComment(baseRef, articleIdSafe) {
  const db = getDatabase();
  const metaSnap = await get(ref(db, `articleComments/${articleIdSafe}/meta/commentCount`));
  const total = typeof metaSnap.val() === 'number' ? metaSnap.val() : 0;
  if (total < MIN_COMMENTS_FOR_BEST) return null;

  const topSnap = await get(query(baseRef, orderByChild('likeCount'), limitToLast(1)));

  let maxLikes = -1;
  topSnap.forEach((child) => {
    const v = child.val();
    const lc = typeof v?.likeCount === 'number' ? v.likeCount : 0;
    maxLikes = Math.max(maxLikes, lc);
  });
  if (maxLikes < MIN_LIKES_FOR_BEST) return null;

  const tieSnap = await get(query(baseRef, orderByChild('likeCount'), equalTo(maxLikes)));
  let best = null;
  let bestCreatedAt = Infinity;
  tieSnap.forEach((child) => {
    const row = { id: child.key, ...(child.val() || {}) };
    if (!isCommentNodeVisible(row)) return;
    const ct = typeof row.createdAt === 'number' ? row.createdAt : 0;
    if (ct < bestCreatedAt) {
      bestCreatedAt = ct;
      best = row;
    }
  });

  return best;
}

/** Row 0 = best; next rows are `batch` minus best so total length is at most PAGE_SIZE. */
function mergePinnedBest(best, batch, pageSize) {
  if (!best || !isCommentNodeVisible(best)) return batch;
  const restCap = Math.max(0, pageSize - 1);
  const rest = batch.filter((c) => c.id !== best.id).slice(0, restCap);

  return [best, ...rest];
}

function pickBestCommentForMock(comments) {
  const visible = comments.filter(isCommentNodeVisible);
  if (visible.length < MIN_COMMENTS_FOR_BEST) return null;
  const candidates = visible.filter((c) => (typeof c.likeCount === 'number' ? c.likeCount : 0) >= MIN_LIKES_FOR_BEST);
  if (!candidates.length) return null;
  return candidates.reduce((acc, c) => {
    const lc = typeof c.likeCount === 'number' ? c.likeCount : 0;
    const al = typeof acc.likeCount === 'number' ? acc.likeCount : 0;
    if (lc > al) return c;
    if (lc < al) return acc;
    const ct = typeof c.createdAt === 'number' ? c.createdAt : 0;
    const at = typeof acc.createdAt === 'number' ? acc.createdAt : 0;
    return ct < at ? c : acc;
  });
}

/**
 * Dev: set `VITE_REVIEW_WIDGET_MOCK=1` in `.env.local` and rebuild to seed the list (skips Firebase load).
 * `handleLike` runs locally: toggles `likes[customerId]`, `likeCount`, and `sortPopularKey` (no API).
 * Mock BEST: same rules as live (`fetchEligibleBestComment` — total ≥2, like ≥2, tie → earliest).
 * One row uses `authorCustomerId` matching the dev `customerId` in `review-widget.jsx` for "mine" UI.
 */
export const REVIEW_WIDGET_TEST_COMMENTS = [
  {
    id: 'test-1',
    text: '배송도 빠르고 포장 상태가 정말 만족스러웠습니다. 냉장 제품이라 걱정했는데 아이스팩이 꼼꼼해서 도착했을 때도 신선함이 그대로였어요. 조리법이 단계별로 잘 정리돼 있어서 처음 만드는 메뉴도 부담 없이 완성할 수 있었고, 가족이 다 맛있다고 했습니다. 양념 비율이나 굽는 시간 팁이 특히 도움이 됐고, 다음엔 다른 메뉴도 주문해 보려고 합니다. 고객 응대도 친절해서 재구매할 의사가 있어요.',
    authorDisplayName: 'k.i.m**@gmail.com',
    authorCustomerId: '12123123',
    createdAt: Date.now() - 2 * 60 * 60 * 1000,
    avatar: 1,
    likeCount: 3,
    likes: { 9990001: true, 9990002: true },
    sortPopularKey: '0000000003test-1',
    edited: false,
    hidden: false,
  },
  {
    id: 'test-2',
    text: 'The recipe card was easy to follow and the ingredients arrived fresh, well organized, and clearly labeled, which made prep faster than I expected. I was nervous about the marinade timing but the step-by-step notes kept everything on track, and the final dish tasted as good as a restaurant version without feeling heavy. Cleanup was simple because everything was pre-portioned, and there was not much food waste, which I really appreciated. I would happily order again and recommend it to friends who want a home-cooked meal without spending the whole night in the kitchen. Thank you for the great experience overall.',
    authorDisplayName: 's.lee**@outlook.com',
    authorCustomerId: '88001234',
    createdAt: Date.now() - 26 * 60 * 60 * 1000,
    avatar: 2,
    likeCount: 12,
    likes: {},
    sortPopularKey: '0000000012test-2',
    edited: false,
    hidden: false,
  },
  {
    id: 'test-3',
    text: '소스가 살짝 짰지만 재료 퀄리티는 만족입니다. 조리 팁 덕분에 실패 없이 끝났어요.',
    authorDisplayName: 'park**@naver.com',
    authorCustomerId: '77005500',
    createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
    avatar: 3,
    likeCount: 0,
    likes: {},
    sortPopularKey: '0000000000test-3',
    edited: true,
    updatedAt: Date.now() - 2 * 24 * 60 * 60 * 1000,
    hidden: false,
  },
  {
    id: 'test-4',
    text: 'First time ordering—clear instructions and fresh ingredients. Will order again.',
    authorDisplayName: 'h.j**@yahoo.com',
    authorCustomerId: '66004400',
    createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
    avatar: 4,
    likeCount: 7,
    likes: { 9990003: true },
    sortPopularKey: '0000000007test-4',
    edited: false,
    hidden: false,
  },
  {
    id: 'test-hidden',
    text: 'This row is hidden in the storefront (mock).',
    authorDisplayName: 'hidden**@example.com',
    authorCustomerId: '99999999',
    createdAt: Date.now() - 60 * 60 * 1000,
    avatar: 1,
    likeCount: 0,
    likes: {},
    sortPopularKey: '0000000000test-hidden',
    edited: false,
    hidden: true,
  },
];

function buildMockSortedFlat(sortMode, likeOverrides, deletedIds) {
  const del = deletedIds && typeof deletedIds.has === 'function' ? deletedIds : new Set();
  const source = REVIEW_WIDGET_TEST_COMMENTS.filter((c) => !del.has(c.id))
    .map((c) => ({
      ...c,
      ...(likeOverrides[c.id] || {}),
    }))
    .filter(isCommentNodeVisible);
  const best = pickBestCommentForMock(source);
  const others = best ? source.filter((c) => c.id !== best.id) : source;
  let sortedOthers = others;
  if (sortMode === 'newest') {
    sortedOthers = [...others].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } else {
    sortedOthers = [...others].sort(
      (a, b) => (b.likeCount || 0) - (a.likeCount || 0) || String(a.id).localeCompare(String(b.id)),
    );
  }
  return best ? [best, ...sortedOthers] : sortedOthers;
}

// const useReviewWidgetTestData = "1"
const useReviewWidgetTestData = import.meta.env.DEV && String(import.meta.env.VITE_REVIEW_WIDGET_MOCK || '') === '1';

/** Never surface raw API bodies to users. */
function userFacingSubmitError(status, loc) {
  const L = localeObj[loc];
  if (status === 401) return L.submitErrorAuth;
  if (status === 400) return L.submitErrorBadRequest;
  if (status >= 500 && status < 600) return L.submitErrorServer;
  return L.submitError;
}

function userFacingMutationError(status, loc) {
  const L = localeObj[loc];
  if (status === 401) return L.submitErrorAuth;
  if (status === 403) return L.submitErrorBadRequest;
  if (status === 400) return L.submitErrorBadRequest;
  if (status >= 500 && status < 600) return L.submitErrorServer;
  return L.submitError;
}

/** Always English, e.g. "Yesterday at 10:39 AM" (ignores app locale for this line). */
function formatCommentDateEnglish(createdAtMs) {
  if (typeof createdAtMs !== 'number') return '';
  const t = dayjs(createdAtMs).locale('en');
  if (!t.isValid()) return '';
  const dayDiff = t.startOf('day').diff(dayjs().startOf('day').locale('en'), 'day');
  if (dayDiff === 0) {
    return `Today at ${t.format('h:mm A')}`;
  }
  if (dayDiff === -1) {
    return `Yesterday at ${t.format('h:mm A')}`;
  }
  if (dayDiff === 1) {
    return `Tomorrow at ${t.format('h:mm A')}`;
  }
  return t.format('MMM D, YYYY [at] h:mm A');
}

const AVATAR_IMG_CLASS = 'flex-shrink-0 h-10 w-10 md:h-[85px] md:w-[85px] rounded-full object-cover';

/** Stable empty list so `useMemo(..., [avatars])` does not retrigger effects every render. */
const NO_AVATAR_URLS = Object.freeze([]);

function isHttpAvatarUrl(s) {
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Accepts `string[]` or JSON string from `data-avatars`; only http(s) URLs kept. */
function normalizeAvatarsProp(raw) {
  if (raw == null) return NO_AVATAR_URLS;
  if (Array.isArray(raw)) {
    const out = raw.map((x) => String(x).trim()).filter(isHttpAvatarUrl);
    return out.length ? out : NO_AVATAR_URLS;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return NO_AVATAR_URLS;
    try {
      const p = JSON.parse(t);
      if (Array.isArray(p)) return normalizeAvatarsProp(p);
    } catch {
      return NO_AVATAR_URLS;
    }
    return NO_AVATAR_URLS;
  }
  return NO_AVATAR_URLS;
}

function AvatarFromUrl({ url, className = '' }) {
  return <img src={url} alt="" className={`${AVATAR_IMG_CLASS} ${className}`.trim()} loading="lazy" />;
}

const ReviewWidget = (props) => {
  const {
    campaignName,
    customerId,
    email,
    articleId,
    articleHandle,
    articleUrl,
    shopHandle,
    locale,
    allowCommentEdit,
    themeColor,
    btnTxtColor,
    avatars,
    allowOnlyOneComment,
    defaultSort,
    pageSize,
    eventEndsAt,
    selectAvatar,
  } = props;
  const isEventEnded = Number(eventEndsAt) <= new Date().getTime();

  /** Only explicit `true` enables edit UI; omit or `false` hides it. */
  const canEditComments = allowCommentEdit === true;
  /** Only explicit `true` limits the signed-in customer to one visible comment per article. */
  const onlyOneCommentActive = allowOnlyOneComment === true;
  const _locale = locale === 'ko' ? 'ko' : 'en';

  const avatarsDep =
    typeof avatars === 'string' ? avatars : Array.isArray(avatars) ? avatars.map(String).join('\u0001') : '';
  const customAvatarUrls = useMemo(() => normalizeAvatarsProp(avatars), [avatars, avatarsDep]);
  const useCustomAvatars = customAvatarUrls.length > 0;

  const [displayName, setDisplayName] = useState('');
  const articleIdSafe = useMemo(() => sanitizeArticleIdForClient(articleId), [articleId]);
  const customerIdStr = customerId != null ? String(customerId).replace(/[^\d]/g, '') : '';

  const trackReviewWidget = useCallback(
    (eventName, extra = {}) => {
      if (!isAmplitudeHostAllowed()) return;
      const cn = campaignName != null ? String(campaignName).trim() : '';
      const payload = {
        ...(cn ? { campaign_name: cn } : {}),
        ...(articleIdSafe ? { article_id: String(articleIdSafe) } : {}),
        ...(articleHandle ? { article_handle: String(articleHandle) } : {}),
        ...(shopHandle ? { shop_handle: String(shopHandle) } : {}),
        locale: _locale,
        ...extra,
      };
      window.amplitude?.track(eventName, payload);
    },
    [campaignName, articleIdSafe, articleHandle, shopHandle, _locale],
  );

  const [avatar, setAvatar] = useState({
    element: <Avatar1 id="blueGrad" stop1="#42A5F5" stop2="#1565C0" fill1="#blueGrad" fill2="#E3F2FD" />,
    open: false,
  });
  const [avatarId, setAvatarId] = useState(1);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  /** Profanity block on new comment submit; cleared when text is fixed or on send. */
  const [submitProfanityWarning, setSubmitProfanityWarning] = useState(null);

  const [sortMode, setSortMode] = useState(defaultSort);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const [commentPage, setCommentPage] = useState(1);
  const newestCursorRef = useRef(null);
  const popularCursorRef = useRef(null);
  const bestPinnedIdRef = useRef(null);
  /** Page P newest query: `endBefore(oldestKeyOnPage[P-1])` for P≥2. */
  const newestOldestKeyByPageRef = useRef({});
  /** Page P popular query: `startAfter` last row of page P-1. */
  const popularLastByPageRef = useRef({});
  const avatarPickerRef = useRef(null);
  const avatarButtonRef = useRef(null);
  /** RTDB: set on full reload; mock: synced from `comments` (see effect below). */
  const [pinnedBestId, setPinnedBestId] = useState(null);
  const mockLikeOverridesRef = useRef({});
  const mockDeletedCommentIdsRef = useRef(new Set());
  const [mockListVersion, setMockListVersion] = useState(0);
  const [totalCommentCount, setTotalCommentCount] = useState(null);

  const totalPages = useMemo(() => {
    if (useReviewWidgetTestData) {
      const flat = buildMockSortedFlat(sortMode, mockLikeOverridesRef.current, mockDeletedCommentIdsRef.current);
      return Math.max(1, Math.ceil(flat.length / pageSize));
    }
    if (totalCommentCount == null) return 1;
    return Math.max(1, Math.ceil(totalCommentCount / pageSize));
  }, [useReviewWidgetTestData, totalCommentCount, sortMode, mockListVersion]);

  /** Header: full article comment count (meta / mock), not current page length. */
  const totalCommentsHeader = useMemo(() => {
    if (totalCommentCount != null) return totalCommentCount;
    if (useReviewWidgetTestData) {
      return buildMockSortedFlat(sortMode, mockLikeOverridesRef.current, mockDeletedCommentIdsRef.current).length;
    }
    return null;
  }, [totalCommentCount, useReviewWidgetTestData, mockListVersion, sortMode]);

  const [likeBusyId, setLikeBusyId] = useState(null);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState(null);
  const [editProfanityBlocked, setEditProfanityBlocked] = useState(false);
  const [longCommentExpanded, setLongCommentExpanded] = useState(() => ({}));

  const [hasUserCommentOnArticle, setHasUserCommentOnArticle] = useState(false);
  const [oneCommentCheckLoading, setOneCommentCheckLoading] = useState(false);
  const [showAvatarTooltip, setShowAvatarTooltip] = useState(false);

  const refreshUserCommentPresence = useCallback(async () => {
    if (!onlyOneCommentActive || !customerIdStr) {
      setHasUserCommentOnArticle(false);
      setOneCommentCheckLoading(false);
      return;
    }
    if (useReviewWidgetTestData) {
      const mine = REVIEW_WIDGET_TEST_COMMENTS.filter((c) => !mockDeletedCommentIdsRef.current.has(c.id))
        .map((c) => ({
          ...c,
          ...(mockLikeOverridesRef.current[c.id] || {}),
        }))
        .some((c) => String(c.authorCustomerId ?? '') === customerIdStr);
      setHasUserCommentOnArticle(mine);
      setOneCommentCheckLoading(false);
      return;
    }
    if (!articleIdSafe || !isFirebaseArticleCommentsConfigured() || !initFirebaseArticleComments()) {
      setHasUserCommentOnArticle(false);
      setOneCommentCheckLoading(false);
      return;
    }
    const base = articleCommentsRef(articleIdSafe);
    if (!base) {
      setHasUserCommentOnArticle(false);
      setOneCommentCheckLoading(false);
      return;
    }
    setOneCommentCheckLoading(true);
    try {
      const q = query(base, orderByChild('authorCustomerId'), equalTo(customerIdStr), limitToFirst(64));
      const snap = await get(q);
      let found = false;
      snap.forEach((child) => {
        const row = child.val() || {};
        if (typeof row.text === 'string' && row.text.length > 0) found = true;
      });
      setHasUserCommentOnArticle(found);
    } catch (err) {
      console.error('ReviewWidget: one-comment presence check failed', err);
      setHasUserCommentOnArticle(false);
    } finally {
      setOneCommentCheckLoading(false);
    }
  }, [onlyOneCommentActive, customerIdStr, articleIdSafe, useReviewWidgetTestData]);

  useEffect(() => {
    if (!onlyOneCommentActive) {
      setHasUserCommentOnArticle(false);
      setOneCommentCheckLoading(false);
      return;
    }
    void refreshUserCommentPresence();
  }, [onlyOneCommentActive, refreshUserCommentPresence, mockListVersion]);

  const newCommentComposerLocked =
    onlyOneCommentActive && Boolean(customerIdStr) && (oneCommentCheckLoading || hasUserCommentOnArticle);

  const fetchMetaCount = useCallback(async () => {
    if (useReviewWidgetTestData) return;
    if (!articleIdSafe || !initFirebaseArticleComments()) return;
    const snap = await get(ref(getDatabase(), `articleComments/${articleIdSafe}/meta/commentCount`));
    const v = snap.val();
    setTotalCommentCount(typeof v === 'number' ? v : null);
  }, [articleIdSafe]);

  const loadCommentsForPage = useCallback(
    async (page) => {
      if (useReviewWidgetTestData) return;
      if (!articleIdSafe || page < 1) return;
      if (!isFirebaseArticleCommentsConfigured()) {
        setCommentsError(localeObj[_locale].commentsNotConfigured);
        return;
      }
      if (!initFirebaseArticleComments()) {
        setCommentsError(localeObj[_locale].commentsNotConfigured);
        return;
      }
      const base = articleCommentsRef(articleIdSafe);
      if (!base) return;

      setCommentsLoading(true);
      setCommentsError(null);
      try {
        if (page === 1) {
          newestCursorRef.current = null;
          popularCursorRef.current = null;
          bestPinnedIdRef.current = null;
          newestOldestKeyByPageRef.current = {};
          popularLastByPageRef.current = {};
          setPinnedBestId(null);
          setComments([]);
        }

        let best = null;
        if (page === 1) {
          best = await fetchEligibleBestComment(base, articleIdSafe);
          bestPinnedIdRef.current = best?.id ?? null;
        }

        const bestIdForDedupe = bestPinnedIdRef.current;

        const firstPageExtra = page === 1 && best != null ? 1 : 0;
        const fetchLimit = page === 1 ? pageSize + firstPageExtra : pageSize;

        if (sortMode === 'newest') {
          let endKey = null;
          if (page >= 2) {
            endKey = newestOldestKeyByPageRef.current[page - 1];
            if (!endKey) {
              setCommentsError(localeObj[_locale].commentsNotConfigured);
              return;
            }
          }
          const q = endKey
            ? query(base, orderByKey(), endBefore(endKey), limitToLast(pageSize))
            : query(base, orderByKey(), limitToLast(fetchLimit));
          const snap = await get(q);
          const rawBatch = [];
          snap.forEach((child) => {
            rawBatch.push({ id: child.key, ...(child.val() || {}) });
          });
          rawBatch.reverse();
          const batch = rawBatch.filter(isCommentNodeVisible);

          if (rawBatch.length) {
            newestCursorRef.current = rawBatch[rawBatch.length - 1].id;
            newestOldestKeyByPageRef.current[page] = rawBatch[rawBatch.length - 1].id;
          } else {
            delete newestOldestKeyByPageRef.current[page];
          }

          Object.keys(newestOldestKeyByPageRef.current).forEach((k) => {
            if (Number(k) > page) delete newestOldestKeyByPageRef.current[k];
          });

          if (page === 1) {
            const merged = mergePinnedBest(best, batch, pageSize);
            setComments(merged);
            setPinnedBestId(best?.id ?? null);
          } else {
            const filtered = bestIdForDedupe ? batch.filter((c) => c.id !== bestIdForDedupe) : batch;
            setComments(filtered);
            setPinnedBestId(null);
          }
        } else {
          let cur = null;
          if (page >= 2) {
            cur = popularLastByPageRef.current[page - 1];
            if (!cur?.key) {
              setCommentsError(localeObj[_locale].commentsNotConfigured);
              return;
            }
          }
          const q = cur
            ? query(base, orderByChild('sortPopularKey'), startAfter(cur.value, cur.key), limitToFirst(pageSize))
            : query(base, orderByChild('sortPopularKey'), limitToFirst(fetchLimit));
          const snap = await get(q);
          const rawBatch = [];
          snap.forEach((child) => {
            rawBatch.push({ id: child.key, ...(child.val() || {}) });
          });
          const batch = rawBatch.filter(isCommentNodeVisible);

          if (rawBatch.length) {
            const last = rawBatch[rawBatch.length - 1];
            popularCursorRef.current = { key: last.id, value: last.sortPopularKey ?? '' };
            popularLastByPageRef.current[page] = {
              key: last.id,
              value: last.sortPopularKey ?? '',
            };
          } else {
            delete popularLastByPageRef.current[page];
          }

          Object.keys(popularLastByPageRef.current).forEach((k) => {
            if (Number(k) > page) delete popularLastByPageRef.current[k];
          });

          if (page === 1) {
            const merged = mergePinnedBest(best, batch, pageSize);
            setComments(merged);
            setPinnedBestId(best?.id ?? null);
          } else {
            const filtered = bestIdForDedupe ? batch.filter((c) => c.id !== bestIdForDedupe) : batch;
            setComments(filtered);
            setPinnedBestId(null);
          }
        }
      } catch (e) {
        console.error(e);
        setPinnedBestId(null);
        setCommentsError(localeObj[_locale].commentsNotConfigured);
      } finally {
        setCommentsLoading(false);
      }
    },
    [articleIdSafe, sortMode, _locale],
  );

  useEffect(() => {
    setCommentPage(1);
  }, [articleIdSafe, sortMode]);

  useEffect(() => {
    if (!useReviewWidgetTestData) return;
    mockLikeOverridesRef.current = {};
  }, [sortMode, useReviewWidgetTestData]);

  useEffect(() => {
    if (useReviewWidgetTestData) {
      const flat = buildMockSortedFlat(sortMode, mockLikeOverridesRef.current, mockDeletedCommentIdsRef.current);
      const total = flat.length;
      const pages = Math.max(1, Math.ceil(total / pageSize));
      const safePage = Math.min(commentPage, pages);
      if (safePage !== commentPage) {
        setCommentPage(safePage);
        return;
      }
      setComments(flat.slice((commentPage - 1) * pageSize, commentPage * pageSize));
      setCommentsLoading(false);
      setCommentsError(null);
      setTotalCommentCount(total);
      return;
    }
    if (!articleIdSafe) return;
    void loadCommentsForPage(commentPage);
  }, [articleIdSafe, sortMode, commentPage, loadCommentsForPage, useReviewWidgetTestData, mockListVersion]);

  useEffect(() => {
    if (commentPage > totalPages) {
      setCommentPage(totalPages);
    }
  }, [commentPage, totalPages]);

  useEffect(() => {
    if (useReviewWidgetTestData) return;
    if (!articleIdSafe) return;
    void fetchMetaCount();
  }, [articleIdSafe, fetchMetaCount]);

  useEffect(() => {
    if (!useReviewWidgetTestData) return;
    const source = REVIEW_WIDGET_TEST_COMMENTS.filter((c) => !mockDeletedCommentIdsRef.current.has(c.id))
      .map((c) => ({
        ...c,
        ...(mockLikeOverridesRef.current[c.id] || {}),
      }))
      .filter(isCommentNodeVisible);
    const best = pickBestCommentForMock(source);
    setPinnedBestId(best?.id ?? null);
  }, [useReviewWidgetTestData, sortMode, mockListVersion]);

  const handleAvatarSelect = (avatar, config) => {
    const id = typeof avatar === 'number' ? avatar : Number(avatar);
    const nextId = Number.isFinite(id) && id >= 1 ? id : 1;
    setAvatarId(nextId);
    if (useCustomAvatars) {
      const idx = nextId - 1;
      const url = customAvatarUrls[idx];
      if (url) {
        setAvatar({ element: <AvatarFromUrl url={url} />, open: false });
        trackReviewWidget('Review Widget Avatar Selected', { avatar_id: nextId });
        return;
      }
    }
    let selected = <Avatar4 />;
    if (avatar === 1) {
      selected = <Avatar1 {...config} />;
    }
    if (avatar === 2) {
      selected = <Avatar2 />;
    }
    if (avatar === 3) {
      selected = <Avatar3 />;
    }
    if (avatar === 4) {
      selected = <Avatar4 />;
    }
    setAvatar({ element: selected, open: false });
    trackReviewWidget('Review Widget Avatar Selected', { avatar_id: nextId });
  };

  const handleAvatarClick = () => {
    setAvatar({
      open: !avatar.open,
      element: avatar.element,
    });
  };

  useEffect(() => {
    if (canEditComments) return;
    setEditingId(null);
    setEditDraft('');
    setEditError(null);
  }, [canEditComments]);

  useEffect(() => {
    if (!avatar.open) return;
    const closeIfOutside = (e) => {
      if (avatarPickerRef.current && !avatarPickerRef.current.contains(e.target)) {
        setAvatar((prev) => ({ ...prev, open: false }));
      }
    };
    document.addEventListener('mousedown', closeIfOutside);
    document.addEventListener('touchstart', closeIfOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      document.removeEventListener('touchstart', closeIfOutside);
    };
  }, [avatar.open]);

  useEffect(() => {
    if (useCustomAvatars && customAvatarUrls[0]) {
      setAvatar((prev) => ({ ...prev, element: <AvatarFromUrl url={customAvatarUrls[0]} /> }));
      setAvatarId(1);
      return;
    }
    setAvatar((prev) => ({
      ...prev,
      element: <Avatar1 id="blueGrad" stop1="#42A5F5" stop2="#1565C0" fill1="#blueGrad" fill2="#E3F2FD" />,
    }));
    setAvatarId(1);
  }, [useCustomAvatars, customAvatarUrls]);

  useEffect(() => {
    if (!submitProfanityWarning) return;
    if (!containsBlockedProfanity(comment)) {
      setSubmitProfanityWarning(null);
    }
  }, [comment, submitProfanityWarning]);

  useEffect(() => {
    if (String(comment).trim().length < MIN_COMMENT_SUBMIT_LENGTH) return;
    setSubmitError((err) => {
      if (err === localeObj.ko.submitTooShort || err === localeObj.en.submitTooShort) return null;
      return err;
    });
  }, [comment]);

  useEffect(() => {
    if (!editProfanityBlocked) return;
    if (!containsBlockedProfanity(editDraft)) {
      setEditProfanityBlocked(false);
      setEditError(null);
    }
  }, [editDraft, editProfanityBlocked]);

  // Avatar tooltip IntersectionObserver
  useEffect(() => {
    const buttonEl = avatarButtonRef.current;
    if (!buttonEl || !selectAvatar) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShowAvatarTooltip(true);
            setTimeout(() => {
              setShowAvatarTooltip(false);
            }, 4000);
            observer.unobserve(buttonEl);
          }
        });
      },
      { threshold: 0.8 },
    );

    observer.observe(buttonEl);

    return () => {
      observer.disconnect();
    };
  }, [selectAvatar]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = String(comment).trim();
    const name = String(displayName).trim();

    if (!name) {
      setSubmitError(localeObj[_locale].nameRequired);
      return;
    }
    if (!text) {
      return;
    }
    if (text.length < MIN_COMMENT_SUBMIT_LENGTH) {
      setSubmitError(localeObj[_locale].submitTooShort);
      return;
    }
    if (onlyOneCommentActive && oneCommentCheckLoading) {
      return;
    }
    if (onlyOneCommentActive && hasUserCommentOnArticle) {
      setSubmitError(localeObj[_locale].oneCommentOnlyNotice);
      return;
    }
    setSubmitError(null);
    setSubmitProfanityWarning(null);
    if (containsBlockedProfanity(text)) {
      setSubmitProfanityWarning(localeObj[_locale].warningProfanityBlocked);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(REVIEW_SUBMIT_URL, {
        method: 'POST',
        headers: reviewSubmitHeaders(),
        body: JSON.stringify({
          customerId: customerId || null,
          email: email || null,
          name: displayName.trim(),
          comment: text,
          avatar: avatarId,
          articleId: articleId || null,
          articleHandle: articleHandle || null,
          articleUrl: articleUrl || null,
          shopHandle: shopHandle || null,
          locale: _locale,
        }),
      });
      if (!res.ok) {
        await res.text().catch(() => {});
        setSubmitError(userFacingSubmitError(res.status, _locale));
        return;
      }
      setComment('');
      setSubmitProfanityWarning(null);
      try {
        await fetchMetaCount();
        if (!useReviewWidgetTestData) {
          if (commentPage === 1) {
            await loadCommentsForPage(1);
          } else {
            setCommentPage(1);
          }
        }
      } catch (refreshErr) {
        console.error('ReviewWidget: post-submit refresh failed', refreshErr);
      }
      trackReviewWidget('Review Widget Comment Submitted');
      if (onlyOneCommentActive) {
        setHasUserCommentOnArticle(true);
      }
    } catch {
      setSubmitError(localeObj[_locale].submitErrorNetwork);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLike = async (c) => {
    if (isEventEnded) return;

    const commentId = c.id;
    if (customerIdStr && String(c.authorCustomerId ?? '') === customerIdStr) {
      return;
    }

    if (useReviewWidgetTestData) {
      if (!customerIdStr) return;
      setLikeBusyId(commentId);
      setCommentsError(null);
      try {
        const flat = buildMockSortedFlat(sortMode, mockLikeOverridesRef.current, mockDeletedCommentIdsRef.current);
        const row = flat.find((x) => x.id === commentId);
        if (!row) return;
        const likes = { ...(row.likes || {}) };
        const wasLiked = Boolean(likes[customerIdStr]);
        if (wasLiked) {
          delete likes[customerIdStr];
        } else {
          likes[customerIdStr] = true;
        }
        const prevCount = typeof row.likeCount === 'number' ? row.likeCount : 0;
        const likeCount = Math.max(0, prevCount + (wasLiked ? -1 : 1));
        const sortPopularKey = `${String(1_000_000_000 - likeCount).padStart(9, '0')}${row.id}`;
        mockLikeOverridesRef.current[commentId] = {
          ...(mockLikeOverridesRef.current[commentId] || {}),
          likes,
          likeCount,
          sortPopularKey,
        };
        setMockListVersion((v) => v + 1);
        trackReviewWidget('Review Widget Like Toggled', {
          comment_id: String(commentId),
          liked: !wasLiked,
        });
      } finally {
        setLikeBusyId(null);
      }
      return;
    }

    if (!customerIdStr || !articleIdSafe) return;

    // Optimistic update: toggle like immediately
    const prevState = { ...c };
    const prevLikes = { ...(c.likes || {}) };
    const wasLiked = Boolean(prevLikes[customerIdStr]);
    const optimisticLikes = { ...prevLikes };

    if (wasLiked) {
      delete optimisticLikes[customerIdStr];
    } else {
      optimisticLikes[customerIdStr] = true;
    }

    const prevCount = typeof c.likeCount === 'number' ? c.likeCount : 0;
    const optimisticLikeCount = Math.max(0, prevCount + (wasLiked ? -1 : 1));

    // Apply optimistic update
    setComments((prev) =>
      prev.map((comment) => {
        if (comment.id !== commentId) return comment;
        return {
          ...comment,
          likes: optimisticLikes,
          likeCount: optimisticLikeCount,
        };
      }),
    );

    // Send request to server
    try {
      const res = await fetch(ARTICLE_COMMENT_ACTION_URL, {
        method: 'POST',
        headers: reviewSubmitHeaders(),
        body: JSON.stringify({
          actionType: 'like',
          articleId: articleIdSafe,
          commentId,
          customerId: customerIdStr,
        }),
      });

      if (!res.ok) {
        // Rollback on error
        setComments((prev) =>
          prev.map((comment) => {
            if (comment.id !== commentId) return comment;
            return prevState;
          }),
        );
        alert(localeObj[_locale].likeError);
        return;
      }

      const data = await res.json();
      const liked = Boolean(data.liked);
      const likeCount = typeof data.likeCount === 'number' ? data.likeCount : 0;
      const sortPopularKey = typeof data.sortPopularKey === 'string' ? data.sortPopularKey : undefined;

      // Confirm with server response
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id !== commentId) return comment;
          const likes = { ...(comment.likes || {}) };
          if (liked) likes[customerIdStr] = true;
          else delete likes[customerIdStr];
          return {
            ...comment,
            likeCount,
            likes,
            ...(sortPopularKey != null ? { sortPopularKey } : {}),
          };
        }),
      );

      trackReviewWidget('Review Widget Like Toggled', {
        comment_id: String(commentId),
        liked,
      });
    } catch {
      // Rollback on network error
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id !== commentId) return comment;
          return prevState;
        }),
      );
      alert(localeObj[_locale].likeError);
    }
  };

  const beginEdit = (c) => {
    if (!canEditComments) return;
    setEditError(null);
    setEditProfanityBlocked(false);
    setEditingId(c.id);
    setEditDraft(typeof c.text === 'string' ? c.text : '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
    setEditError(null);
    setEditProfanityBlocked(false);
  };

  const handleSaveEdit = async (commentId) => {
    if (!canEditComments) return;
    const text = String(editDraft).trim();
    if (!text || !customerIdStr || !articleIdSafe) return;
    setEditError(null);
    setEditProfanityBlocked(false);
    if (containsBlockedProfanity(text)) {
      setEditProfanityBlocked(true);
      setEditError(localeObj[_locale].warningProfanityBlocked);
      return;
    }
    try {
      const res = await fetch(ARTICLE_COMMENT_ACTION_URL, {
        method: 'POST',
        headers: reviewSubmitHeaders(),
        body: JSON.stringify({
          actionType: 'edit',
          articleId: articleIdSafe,
          commentId,
          customerId: customerIdStr,
          comment: text,
        }),
      });
      await res.text().catch(() => {});
      if (!res.ok) {
        setEditProfanityBlocked(false);
        setEditError(userFacingMutationError(res.status, _locale));
        return;
      }
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? { ...c, text, edited: true, updatedAt: Date.now() } : c)),
      );
      cancelEdit();
    } catch {
      setEditProfanityBlocked(false);
      setEditError(localeObj[_locale].submitErrorNetwork);
    }
  };

  const handleDeleteComment = async (c) => {
    const commentId = c?.id;
    if (!commentId || !customerIdStr) return;
    if (String(c.authorCustomerId ?? '') !== customerIdStr) return;
    if (!window.confirm(localeObj[_locale].deleteConfirm)) return;

    if (useReviewWidgetTestData) {
      mockDeletedCommentIdsRef.current.add(commentId);
      if (editingId === commentId) cancelEdit();
      setMockListVersion((v) => v + 1);
      return;
    }

    if (!articleIdSafe) return;
    setDeleteBusyId(commentId);
    setCommentsError(null);
    try {
      const res = await fetch(ARTICLE_COMMENT_ACTION_URL, {
        method: 'POST',
        headers: reviewSubmitHeaders(),
        body: JSON.stringify({
          actionType: 'delete',
          articleId: articleIdSafe,
          commentId,
          customerId: customerIdStr,
        }),
      });
      await res.text().catch(() => {});
      if (!res.ok) {
        setCommentsError(userFacingMutationError(res.status, _locale));
        return;
      }
      setComments((prev) => prev.filter((row) => row.id !== commentId));
      if (editingId === commentId) cancelEdit();
      if (onlyOneCommentActive) {
        setSubmitError(null);
        void refreshUserCommentPresence();
      }
      try {
        await fetchMetaCount();
      } catch (metaErr) {
        console.error('ReviewWidget: post-delete meta refresh failed', metaErr);
      }
    } catch {
      setCommentsError(localeObj[_locale].submitErrorNetwork);
    } finally {
      setDeleteBusyId(null);
    }
  };

  return (
    <div className="w-full h-hull flex flex-col md:max-w-[620px] mx-auto">
      <div className="w-full relative bg-[#FFFBF5] flex flex-col items-center justify-center gap-x-16 gap-y-4 px-5 py-7 text-start">
        <div className="flex gap-x-4">
          <div>✔</div>
          <div>
            <p className="font-bold">{localeObj[_locale].instructionTitle1}</p>
            <p className="font-medium">{localeObj[_locale].instruction1}</p>
          </div>
        </div>

        <div className="flex gap-x-4">
          <div>✔</div>
          <div>
            <p className="font-bold">{localeObj[_locale].instructionTitle2}</p>
            <p className="font-medium">{localeObj[_locale].instruction2}</p>
          </div>
        </div>
      </div>

      {isEventEnded && (
        <div className="relative">
          <div
            className="absolute w-full h-full flex flex-col gap-y-2 justify-center items-center z-10 bg-[#fffefeb3]"
            style={{ backdropFilter: 'blur(6px)' }}
          >
            <p className="font-bold text-2xl text-center">{localeObj[_locale].eventEnd}</p>
          </div>

          <div className="flex flex-col gap-y-2 md:gap-y-0 p-2">
            <div className="flex items-center md:gap-x-4 gap-x-2">
              <div className="bg-gray-400 w-[60px] h-[60px] flex-shrink-0 rounded-full flex relative"></div>

              <div className="bg-gray-400 rounded-full py-2 px-4 text-sm h-8 w-full" />
            </div>

            <div className="md:ml-[76px] ml-0 flex flex-col bg-gray-100 rounded-2xl">
              <div className="bg-gray-400 py-2 px-4 h-40 rounded-2xl pb-9" />
            </div>
          </div>
        </div>
      )}

      {!isEventEnded && customerId && (
        <>
          <form className="flex flex-col gap-y-2 md:gap-y-0 mt-4" onSubmit={handleSubmit}>
            <div className="flex items-center md:gap-x-5 gap-x-2">
              <div className="flex relative flex-shrink-0" ref={avatarPickerRef}>
                {/* Avatar button with tooltip */}
                <button
                  ref={avatarButtonRef}
                  className="flex-shrink-0 disabled:cursor-not-allowed disabled:opacity-60 relative"
                  type="button"
                  onClick={handleAvatarClick}
                  aria-label="Choose avatar"
                  disabled={newCommentComposerLocked || isSubmitting}
                >
                  {avatar.element}

                  {/* Tooltip */}
                  {showAvatarTooltip && selectAvatar && (
                    <div
                      className="absolute bottom-full left-0 mb-2 px-3 py-1.5 bg-white text-black text-sm lg:text-base rounded whitespace-nowrap pointer-events-none z-20 transition-opacity duration-300 opacity-100"
                      style={{ border: '1px solid #8E8E8EC7' }}
                    >
                      {selectAvatar}
                      <div
                        className="absolute top-full left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-white"
                        style={{
                          filter: 'drop-shadow(0 1px 0 #8E8E8EC7)',
                        }}
                      ></div>
                    </div>
                  )}
                </button>

                {avatar.open && (
                  <AvatarPopup
                    className="bottom-[50%] left-[100%] absolute z-10 translate-x-4 translate-y-[50%]"
                    customAvatarUrls={customAvatarUrls}
                    onSelect={handleAvatarSelect}
                    locale={_locale}
                  />
                )}
              </div>

              <input
                type="text"
                className="rounded-[4px] border border-gray-400 py-2 px-4 text-sm h-fit w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
                placeholder={localeObj[_locale].namePh}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={isSubmitting || newCommentComposerLocked}
                required
                maxLength={50}
              />
            </div>

            <div className="md:ml-[104px] ml-12 mb-2 flex flex-col rounded-[4px] border border-gray-400 focus-visible:ring focus-within:ring">
              <textarea
                className="py-2 px-4 rounded-[4px] text-sm pb-9 focus-visible:outline-none focus-within:outline-none bg-transparent disabled:cursor-not-allowed disabled:opacity-60"
                style={{ resize: 'none' }}
                placeholder={localeObj[_locale].reviewPh}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                name="comment"
                disabled={isSubmitting || newCommentComposerLocked}
                aria-invalid={submitProfanityWarning ? 'true' : undefined}
              />
            </div>

            <div className="md:ml-[104px] ml-12 ">
              {submitProfanityWarning ? (
                <p
                  role="alert"
                  className="mx-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 my-2"
                >
                  {submitProfanityWarning}
                </p>
              ) : null}
              {onlyOneCommentActive && oneCommentCheckLoading ? (
                <p className="mx-2 px-3 py-2 text-sm text-gray-500 my-2">{localeObj[_locale].oneCommentOnlyChecking}</p>
              ) : null}
              {onlyOneCommentActive && !oneCommentCheckLoading && hasUserCommentOnArticle ? (
                <p
                  role="status"
                  className="mx-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 my-2"
                >
                  {localeObj[_locale].oneCommentOnlyNotice}
                </p>
              ) : null}
              {submitError ? (
                <p role="alert" className="px-4 text-sm text-red-600 my-2">
                  {submitError}
                </p>
              ) : null}
            </div>

            <div className="flex w-full">
              <button
                type="submit"
                className={cls(
                  'ml-auto px-3 rounded-[10px] bg-[#EB3A6C] text-white font-medium',
                  'disabled:opacity-50 w-full text-sm md:text-xl py-3 md:py-3',
                )}
                style={{ backgroundColor: themeColor, color: btnTxtColor ?? '#fff' }}
                disabled={
                  isSubmitting ||
                  newCommentComposerLocked ||
                  String(comment).trim().length < MIN_COMMENT_SUBMIT_LENGTH ||
                  !String(displayName).trim()
                }
              >
                {isSubmitting ? localeObj[_locale].submitting : localeObj[_locale].submit}
              </button>
            </div>
          </form>

          {/* <div className="flex w-full items-center justify-center gap-x-1 mt-2"> */}
          {/*   <div className="bg-[#FF7B65] rounded-[10px] md:rounded-xl text-center px-2 py-[2px]"> */}
          {/*     <p className="text-white text-xs md:text-sm">TIP</p> */}
          {/*   </div> */}
          {/**/}
          {/*   <p className="text-[#8E8E8E] text-xs md:text-sm">{localeObj[_locale].tip}</p> */}
          {/* </div> */}
        </>
      )}

      <div className="mb-4 mt-10 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center">
          <span className="font-medium text-2xl mr-1">{localeObj[_locale].totalComments}</span>
          <span>{totalCommentsHeader ?? '…'}</span>
        </div>

        <div
          className="flex gap-1 text-sm"
          style={{
            flexDirection: sortMode === 'popular' ? 'row' : 'row-reverse',
          }}
        >
          <button
            type="button"
            className={`flex gap-x-1 items-center rounded-lg p-1 ${
              sortMode === 'popular' ? 'font-bold text-[#2B2B2B]' : 'text-[#8E8E8E]'
            }`}
            style={{ viewTransitionName: 'sort-popular' }}
            onClick={() => {
              const apply = () => {
                if (sortMode !== 'popular') {
                  trackReviewWidget('Review Widget Sort Changed', { sort_mode: 'popular' });
                }
                setSortMode('popular');
              };
              if (!document.startViewTransition) {
                apply();
                return;
              }
              document.startViewTransition(apply);
            }}
          >
            {sortMode === 'popular' && <FaCheck />}
            {localeObj[_locale].sortPopular}
          </button>
          <button
            type="button"
            className={`flex gap-x-1 items-center rounded-lg p-1 ${
              sortMode === 'newest' ? 'font-bold text-[#2B2B2B]' : 'text-[#8E8E8E]'
            }`}
            style={{ viewTransitionName: 'sort-newest' }}
            onClick={() => {
              const apply = () => {
                if (sortMode !== 'newest') {
                  trackReviewWidget('Review Widget Sort Changed', { sort_mode: 'newest' });
                }
                setSortMode('newest');
              };
              if (!document.startViewTransition) {
                apply();
                return;
              }
              document.startViewTransition(apply);
            }}
          >
            {sortMode === 'newest' && <FaCheck />}
            {localeObj[_locale].sortNewest}
          </button>
        </div>
      </div>

      {!isEventEnded && !customerId && (
        <div className="relative">
          <div
            className="absolute w-full h-full flex flex-col gap-y-2 justify-center items-center z-10 bg-[#fffefeb3]"
            style={{ backdropFilter: 'blur(6px)' }}
          >
            <p className="font-bold text-2xl text-center">{localeObj[_locale].needLogin}</p>
            <a
              className="font-bold text-base underline"
              href={
                window.location.origin +
                window.Shopify.routes.root +
                'account/login?return_url=' +
                window.location.pathname
              }
            >
              {localeObj[_locale].goToLogin}
            </a>
          </div>

          <div className="flex flex-col gap-y-2 md:gap-y-0 p-2">
            <div className="flex items-center md:gap-x-4 gap-x-2">
              <div className="bg-gray-400 w-[60px] h-[60px] flex-shrink-0 rounded-full flex relative"></div>

              <div className="bg-gray-400 rounded-full py-2 px-4 text-sm h-8 w-full" />
            </div>

            <div className="md:ml-[76px] ml-0 flex flex-col bg-gray-100 rounded-2xl">
              <div className="bg-gray-400 py-2 px-4 h-40 rounded-2xl pb-9" />
            </div>
          </div>
        </div>
      )}

      <div className="w-full h-full my-10">
        {commentsError && <p className="mb-4 text-sm text-red-600">{commentsError}</p>}
        {commentsLoading && comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center">…</p>
        ) : comments.length === 0 ? (
          <p className="text-gray-500 text-sm text-center">{localeObj[_locale].noComments}</p>
        ) : (
          <ul className="flex flex-col w-full h-full gap-y-8 list-none">
            {comments.map((c, i) => {
              const isMine = customerIdStr && String(c.authorCustomerId ?? '') === customerIdStr;
              const av = Number(c.avatar);
              const avIdx = (Math.floor(Number.isFinite(av) ? av : 1) || 1) - 1;
              const customUrl =
                useCustomAvatars && avIdx >= 0 && avIdx < customAvatarUrls.length ? customAvatarUrls[avIdx] : null;
              const avatarEl = customUrl ? (
                <AvatarFromUrl url={customUrl} />
              ) : av === 2 ? (
                <Avatar2 />
              ) : av === 3 ? (
                <Avatar3 />
              ) : av === 4 ? (
                <Avatar4 />
              ) : (
                <Avatar1 id="blueGrad" stop1="#42A5F5" stop2="#1565C0" fill1="#blueGrad" fill2="#E3F2FD" />
              );
              const liked = Boolean(c.likes && c.likes[customerIdStr]);
              const when = formatCommentDateEnglish(c.createdAt);
              const showBestBadge = pinnedBestId != null && c.id === pinnedBestId;
              const bestBadgeEl = showBestBadge ? (
                <span className="mr-1 inline-block shrink-0 rounded-[10px] bg-[#EB3A6C] px-[6px] py-[3px] text-xs text-white align-top">
                  BEST
                </span>
              ) : null;

              return (
                <li
                  key={c.id}
                  className={`flex gap-x-4 md:gap-x-5 rounded-xl p-2 -m-2 ${
                    isMine ? 'bg-amber-50 ring-1 ring-amber-200' : ''
                  }`}
                >
                  <div className="flex-shrink-0">{avatarEl}</div>
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span
                        className="font-medium text-base truncate max-w-[150px] md:max-w-[600px]"
                        title={c.authorDisplayName}
                      >
                        {c.authorDisplayName || '—'}
                      </span>
                      <span className="text-gray-400 text-sm">{when}</span>
                      {c.edited ? (
                        <span className="text-gray-400 text-xs">{localeObj[_locale].editedLabel}</span>
                      ) : null}
                    </div>
                    {editingId === c.id && canEditComments ? (
                      <div className="mt-2 flex flex-col gap-2">
                        <div className="flex items-start gap-x-1">
                          {bestBadgeEl}
                          <textarea
                            className="min-w-0 flex-1 rounded-lg border border-gray-300 p-2 text-sm bg-transparent"
                            rows={3}
                            value={editDraft}
                            onChange={(e) => setEditDraft(e.target.value)}
                            aria-invalid={editProfanityBlocked ? 'true' : undefined}
                          />
                        </div>
                        {editError ? (
                          <p
                            role="alert"
                            className={
                              editProfanityBlocked
                                ? 'rounded-lg border border-amber-300 bg-amber-50 px-2 py-2 text-sm text-amber-950'
                                : 'text-sm text-red-600'
                            }
                          >
                            {editError}
                          </p>
                        ) : null}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className="rounded-lg bg-[#EB3A6C] px-3 py-1 text-sm text-white"
                            onClick={() => handleSaveEdit(c.id)}
                          >
                            {localeObj[_locale].save}
                          </button>
                          <button type="button" className="rounded-lg border px-3 py-1 text-sm" onClick={cancelEdit}>
                            {localeObj[_locale].cancel}
                          </button>
                        </div>
                      </div>
                    ) : (
                      (() => {
                        const fullText = typeof c.text === 'string' ? c.text : '';
                        const isLong = fullText.length > COMMENT_TEXT_COLLAPSE_AT;
                        const isOpen = Boolean(longCommentExpanded[c.id]);
                        if (!isLong) {
                          return (
                            <p className="mt-1" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {bestBadgeEl}
                              {fullText}
                            </p>
                          );
                        }
                        if (isOpen) {
                          return (
                            <p className="mt-1" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {bestBadgeEl}
                              {fullText}
                            </p>
                          );
                        }
                        return (
                          <p className="mt-1" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {bestBadgeEl}
                            {fullText.slice(0, COMMENT_TEXT_COLLAPSE_AT)}...
                            <button
                              type="button"
                              className="ml-1 inline text-sm text-[#767676]"
                              onClick={() => {
                                trackReviewWidget('Review Widget Comment Expanded', { comment_id: String(c.id) });
                                setLongCommentExpanded((prev) => ({ ...prev, [c.id]: true }));
                              }}
                            >
                              {localeObj[_locale].showAll}
                            </button>
                          </p>
                        );
                      })()
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                      {isMine && editingId !== c.id ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[#767676]">
                          {canEditComments ? (
                            <div className="flex items-center">
                              <LiaEdit className="text-[15px]" />
                              <button type="button" className="underline text-gray-700" onClick={() => beginEdit(c)}>
                                {localeObj[_locale].edit}
                              </button>
                            </div>
                          ) : null}
                          <div className="flex items-center">
                            <RiDeleteBinLine className="text-[15px]" />
                            <button
                              type="button"
                              className="underline text-gray-700 disabled:opacity-50"
                              disabled={deleteBusyId === c.id}
                              onClick={() => handleDeleteComment(c)}
                            >
                              {localeObj[_locale].delete}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col text-center">
                    {liked ? (
                      <button
                        type="button"
                        disabled={!customerIdStr || likeBusyId === c.id || isMine}
                        onClick={() => handleLike(c)}
                        style={{ viewTransitionName: `filled-like-btn-${i}` }}
                      >
                        <VscHeartFilled className="w-5 h-5 md:w-8 md:h-8 text-[#F15837]" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={!customerIdStr || likeBusyId === c.id || isMine}
                        onClick={() => handleLike(c)}
                        style={{ viewTransitionName: `empty-like-btn-${i}` }}
                      >
                        <VscHeart className="w-5 h-5 md:w-8 md:h-8 text-[#C4C4C4]" />
                      </button>
                    )}
                    <span className="text-sm text-[#C4C4C4]">{typeof c.likeCount === 'number' ? c.likeCount : 0}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {useReviewWidgetTestData || (articleIdSafe && isFirebaseArticleCommentsConfigured()) ? (
          <div className="mt-10 flex w-full items-center justify-center">
            <button
              type="button"
              className="rounded-sm border border-[#8E8E8E] bg-white p-1 shadow-sm disabled:opacity-40"
              disabled={commentsLoading || commentPage <= 1}
              aria-label="Previous page"
              onClick={() => setCommentPage((p) => Math.max(1, p - 1))}
            >
              <HiChevronLeft className="stroke-1" />
            </button>
            <div className="mx-4 flex gap-x-1 text-sm">
              <span>{commentPage}</span>
              <span>/</span>
              <span>{totalPages}</span>
            </div>
            <button
              type="button"
              className="rounded-sm border border-[#8E8E8E] bg-white p-1 shadow-sm disabled:opacity-40"
              disabled={commentsLoading || commentPage >= totalPages}
              aria-label="Next page"
              onClick={() => setCommentPage((p) => Math.min(totalPages, p + 1))}
            >
              <HiChevronRight className="stroke-1" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const AVATAR_POPUP_ITEM_RING =
  'col-span-1 justify-self-center inline-flex shrink-0 rounded-full [outline:2px_solid_transparent] outline-offset-2 transition-[outline-color] duration-300 ease-out hover:[outline-color:#F15837] !h-10 !w-10 md:!h-[50px] md:!w-[50px]';

const AvatarPopup = (props) => {
  const urls = Array.isArray(props.customAvatarUrls) ? props.customAvatarUrls : [];
  if (urls.length > 0) {
    return (
      <div className={`${props.className} rounded-xl`} style={{ boxShadow: '1px 0px 6px 0px #0000003b' }}>
        <div className="grid grid-cols-4 gap-2 bg-white zoomIn py-2 px-2 rounded-xl w-[240px]">
          {urls.map((url, i) => (
            <div key={`${url}-${i}`} className={AVATAR_POPUP_ITEM_RING}>
              <button
                type="button"
                className="cursor-pointer rounded-full "
                onClick={() => props.onSelect(i + 1)}
                aria-label={`Avatar ${i + 1}`}
              >
                <AvatarFromUrl url={url} className="!h-10 !w-10 md:!h-[50px] md:!w-[50px]" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div
      className={`zoomIn bg-white grid grid-cols-4 gap-2 h-fit w-fit py-2 px-2 rounded-xl ${props.className}`}
      style={{ boxShadow: '1px 0px 6px 0px #0000003b' }}
    >
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar1
          className="!w-10 !h-10 cursor-pointer"
          id="blueGrad"
          stop1="#42A5F5"
          stop2="#1565C0"
          fill1="#blueGrad"
          fill2="#E3F2FD"
          onClick={props.onSelect}
        />
      </div>
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar1
          className="!w-10 !h-10 cursor-pointer"
          id="pinkGrad"
          stop1="#FF80AB"
          stop2="#F50057"
          fill1="#pinkGrad"
          fill2="#FCE4EC"
          onClick={props.onSelect}
        />
      </div>
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar1
          className="!w-10 !h-10 cursor-pointer"
          id="yellowGrad"
          stop1="#FFE57F"
          stop2="#FFA000"
          fill1="#yellowGrad"
          fill2="#FFFDE7"
          onClick={props.onSelect}
        />
      </div>
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar2 className="!w-10 !h-10 cursor-pointer" onClick={props.onSelect} />
      </div>
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar3 className="!w-10 !h-10 cursor-pointer" onClick={props.onSelect} />
      </div>
      <div className={AVATAR_POPUP_ITEM_RING}>
        <Avatar4 className="!w-10 !h-10 cursor-pointer" size={40} onClick={props.onSelect} />
      </div>
    </div>
  );
};

const Avatar1 = (props) => {
  const { id, stop1, stop2, fill1, fill2 } = props;
  const handleClick = () => {
    if (props.onClick) {
      props.onClick(1, {
        id,
        stop1,
        stop2,
        fill1,
        fill2,
      });
    }
  };
  return (
    <svg
      className={`flex-shrink-0 h-10 w-10 md:w-[50px] md:h-[50px] ${props.className}`}
      onClick={handleClick}
      width="60"
      height="60"
      viewBox="0 0 60 60"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: stop1 }} />
          <stop offset="100%" style={{ stopColor: stop2 }} />
        </linearGradient>
      </defs>
      <circle cx="30" cy="30" r="30" fill={`url(${fill1})`} />
      <g fill={fill2}>
        <path d="M30,12a9,9 0 1,0 0,18a9,9 0 0,0 0,-18z" />
        <path d="M30,33a15,13 0 0,0 -15,13v3h30v-3a15,13 0 0,0 -15,-13z" />
      </g>
    </svg>
  );
};

const Avatar2 = (props) => {
  return (
    <svg
      className={`flex-shrink-0 h-10 w-10 md:w-[50px] md:h-[50px] ${props.className}`}
      onClick={() => props.onClick && props.onClick(2)}
      width="60"
      height="60"
      viewBox="0 0 60 60"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="auroraGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#A259FF' }} />
          <stop offset="50%" style={{ stopColor: '#00D2FF' }} />
          <stop offset="100%" style={{ stopColor: '#00FFA3' }} />
        </linearGradient>
      </defs>
      <circle cx="30" cy="30" r="30" fill="url(#auroraGrad)" />
      <g fill="#F0FFF8">
        <path d="M30,12a9,9 0 1,0 0,18a9,9 0 0,0 0,-18z" />
        <path d="M30,33a15,13 0 0,0 -15,13v3h30v-3a15,13 0 0,0 -15,-13z" />
      </g>
    </svg>
  );
};

const Avatar3 = (props) => {
  return (
    <svg
      className={`flex-shrink-0 h-10 w-10 md:w-[50px] md:h-[50px] ${props.className}`}
      onClick={() => props.onClick && props.onClick(3)}
      width="60"
      height="60"
      viewBox="0 0 60 60"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="sunsetGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#FF6B6B' }} />
          <stop offset="40%" style={{ stopColor: '#FF8E53' }} />
          <stop offset="70%" style={{ stopColor: '#FFC371' }} />
          <stop offset="100%" style={{ stopColor: '#C34B97' }} />
        </linearGradient>
      </defs>
      <circle cx="30" cy="30" r="30" fill="url(#sunsetGrad)" />
      <g fill="#FFF5F0">
        <path d="M30,12a9,9 0 1,0 0,18a9,9 0 0,0 0,-18z" />
        <path d="M30,33a15,13 0 0,0 -15,13v3h30v-3a15,13 0 0,0 -15,-13z" />
      </g>
    </svg>
  );
};

const Avatar4 = (props) => {
  let size = props.size ? Number(props.size) : 50;

  const isMobile = window.innerWidth < 786;
  if (isMobile) {
    size = 40;
  }

  return (
    <div
      className={`rounded-full flex-shrink-0 h-10 w-10 md:w-[50px] md:h-[50px] ${props.className}`}
      onClick={() => props.onClick && props.onClick(4)}
      style={{
        background:
          "url('https://cdn.shopify.com/s/files/1/0528/8266/9745/files/wooltari_logo.png?v=1775745881&width=200&height=200&crop=center')",
        backgroundSize: `${size + 20}px ${size + 20}px`,
        backgroundPosition: 'center',
      }}
    />
  );
};

export default ReviewWidget;